import * as Sentry from '@sentry/browser';
import { initializeApp } from 'firebase/app';
// eslint-disable-next-line import/named
import { User, getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { FirebaseError } from '@firebase/util';

import config from '../../config/config.json';
import { DEBUG_MODE } from '../spdConfig';

// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional

const firebaseConfig = {
  apiKey: config.FIREBASE.API_KEY,
  authDomain: config.FIREBASE.AUTH_DOMAIN,
  projectId: config.FIREBASE.PROJECT_ID,
  storageBucket: config.FIREBASE.STORAGE_BUCKET,
  messagingSenderId: config.FIREBASE.MESSAGING_SENDER_ID,
  appId: config.FIREBASE.APP_ID,
};

export class AuthError {
  constructor(public code: string, public message: string) {}
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);
// Initialize Firebase Authentication and get a reference to the service
const auth = getAuth(app);

const emitSpdSignOut = () => {
  window.dispatchEvent(new CustomEvent('spd-signout'));
};

export const getActiveToken = async () => {
  const currentUser = auth.currentUser;
  if (currentUser) {
    const token = await currentUser.getIdToken();
    return token;
  } else {
    return '';
  }
};

export const getActiveUser = async () => {
  const user = auth.currentUser;
  if (user) {
    const decodedToken = await user.getIdTokenResult();
    return {
      displayName: user.displayName,
      email: user.email,
      roles: decodedToken.claims.roles,
    };
  }
  return null;
};

export const signOut = async () => {
  // Wspólny punkt wylogowania (tools + SPD.auth):
  // sygnalizuje konieczność domknięcia streamingu i kamer.
  emitSpdSignOut();
  try {
    await auth.signOut();
    DEBUG_MODE && console.log('signOut user');
  } catch (error) {
    DEBUG_MODE && console.error('Error signOut user', error);
    Sentry.captureException(error);
  }
};

export const signOutOnlyPublicUser = async () => {
  const user = auth.currentUser;
  if (user) {
    const decodedToken = await user.getIdTokenResult();

    if (decodedToken.claims.roles?.includes(Role.Public)) {
      await signOut();
    }
  }
};

enum Role {
  Admin = 'Admin',
  Inactive = 'Inactive',
  Examined = 'Examined',
  PsychologistAssessor = 'PsychologistAssessor',
  PsychologistSpecialist = 'PsychologistSpecialist',
  MedicalAssessor = 'MedicalAssessor',
  MedicalSpecialist = 'MedicalSpecialist',
  MedicalRegistration = 'MedicalRegistration',
  Public = 'Public',
}

const requiredRolesForExam = [Role.Examined];
const requiredRolesForPublicExam = [Role.Public, Role.Examined];

const requiredRolesForSpecialist = [
  Role.Admin,
  Role.PsychologistAssessor,
  Role.PsychologistSpecialist,
  Role.MedicalSpecialist,
  Role.MedicalAssessor,
];

export enum AuthStatus {
  CorrectAuth = 0,
  WrongAuth = 1,
  WrongRole = 2,
}

export const signIn = async (
  email: string,
  psw: string,
): Promise<AuthStatus> => {
  try {
    await auth.signOut();

    const userCredential = await signInWithEmailAndPassword(auth, email, psw);
    const user = userCredential.user;
    DEBUG_MODE &&
      console.log('Autoryzowano', userCredential, await user.getIdToken());
    const goodRole = await checkRoleForUser(user, false);
    return goodRole ? AuthStatus.CorrectAuth : AuthStatus.WrongRole;
  } catch (error) {
    //activeToken = '';
    DEBUG_MODE && console.log('Error signIn', error);
    if (error instanceof FirebaseError) {
      throw new AuthError(error.code, error.message);
    }
    throw new AuthError('other', '');
  }
};

export const publicSignIn = async (): Promise<AuthStatus> => {
  try {
    await auth.signOut();

    const userCredential = await signInWithEmailAndPassword(
      auth,
      config.PUBLIC_EMAIL,
      config.PUBLIC_PSW,
    );
    const user = userCredential.user;
    DEBUG_MODE &&
      console.log(
        'Autoryzowano publicznie',
        userCredential,
        await user.getIdToken(),
      );
    const goodRole = await checkRoleForPublicUser(user);
    return goodRole ? AuthStatus.CorrectAuth : AuthStatus.WrongRole;
  } catch (error) {
    //activeToken = '';
    DEBUG_MODE && console.log('Error publicSignIn', error);
    if (error instanceof FirebaseError) {
      throw new AuthError(error.code, error.message);
    }
    throw new AuthError('other', '');
  }
};

async function checkRoleForUser(user: User, specialist: boolean) {
  const decodedToken = await user.getIdTokenResult();

  const containsRolesForSpecialist = requiredRolesForSpecialist.some(role =>
    decodedToken.claims.roles.includes(role),
  );
  const containsRolesForExamined = requiredRolesForExam.some(role =>
    decodedToken.claims.roles.includes(role),
  );

  const valid = specialist
    ? containsRolesForSpecialist
    : containsRolesForExamined;

  DEBUG_MODE &&
    console.log('Decoded roles', decodedToken.claims.roles, 'Valid', valid);

  return valid;
}

async function checkRoleForPublicUser(user: User) {
  const decodedToken = await user.getIdTokenResult();

  const valid = requiredRolesForPublicExam.some(role =>
    decodedToken.claims.roles.includes(role),
  );

  DEBUG_MODE &&
    console.log('Decoded roles', decodedToken.claims.roles, 'Valid', valid);

  return valid;
}

export function checkAuthState(
  specialist: boolean,
  publicUser: boolean,
  callbackAfterLogin: (() => void) | null,
  callbackToLogin: () => void,
) {
  let callbackToLoginCalled = false;
  let callbackAfterLoginCalled = false;

  // eslint-disable-next-line complexity
  auth.onAuthStateChanged(async user => {
    try {
      if (user) {
        DEBUG_MODE && console.log('onAuthStateChanged: Zalogowany użytkownik');
        callbackToLoginCalled = false;
        const valid = publicUser
          ? await checkRoleForPublicUser(user)
          : await checkRoleForUser(user, specialist);
        if (valid) {
          if (!callbackAfterLoginCalled) {
            callbackAfterLogin && callbackAfterLogin();
            callbackAfterLoginCalled = true;
          }
          return;
        }
      }
      DEBUG_MODE && console.log('onAuthStateChanged: Wylogowany użytkownik');
      if (!callbackToLoginCalled) {
        emitSpdSignOut();
        callbackToLogin && callbackToLogin();
        callbackToLoginCalled = true;
      }
    } catch (e) {
      console.error('onAuthStateChanged error check role');
      Sentry.captureException(e);
      emitSpdSignOut();
      callbackToLogin && callbackToLogin();
      callbackToLoginCalled = true;
    }
  });

  auth.onIdTokenChanged(async user => {
    try {
      if (user) {
        DEBUG_MODE && console.log('onIdTokenChanged: aktualizacja Tokenu');
        callbackToLoginCalled = false;
        const valid = publicUser
          ? await checkRoleForPublicUser(user)
          : await checkRoleForUser(user, specialist);
        if (valid) {
          /// token po aktualizacji wciąż pozwala pracować nic nie robię
          ///callbackAfterLogin && callbackAfterLogin();
          return;
        }
      }
      DEBUG_MODE && console.log('onIdTokenChanged: Wylogowany użytkownik');
      if (!callbackToLoginCalled) {
        emitSpdSignOut();
        callbackToLogin && callbackToLogin();
        callbackToLoginCalled = true;
      }
    } catch (e) {
      console.error('onIdTokenChanged error check role');
      Sentry.captureException(e);
      emitSpdSignOut();
      callbackToLogin && callbackToLogin();
      callbackToLoginCalled = true;
    }
  });
}
